import React from 'react';
import { isEmpty } from 'lodash';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { Grid, Typography } from '@mui/material';
import { type GridColDef } from '@mui/x-data-grid-premium';

import { Icon, showDialogGenericInfo, TabFab, Utils } from 'app/main/components';
import { usePagination, useSelection, useSettings } from 'app/main/components/hooks';
import { ViewTable, ViewTableUtils } from 'app/main/components/page-layouts';

import DemsDetailsSectionAddRecipients from '../sections/DemsDetailsSectionAddRecipients';
import DemsDetailsSectionDeleteRecipients from '../sections/DemsDetailsSectionDeleteRecipients';
import { showDemsRecipientsOpenDetailsDialog } from '../../dems-recipients/DemsRecipientsOpenDetailsDialog';
import { showDemsRecipientsClickDetailsDialog } from '../../dems-recipients/DemsRecipientsClickDetailsDialog';

export default function DemsRecipientsTab({ model }: { model: any }) {
	const dispatch = useDispatch();
	const { t } = useSettings();
	const intl = useIntl();

	// Can be edited only if draft
	const canEdit = model.dems_dmst_id === 1;

	const [refresh, setRefresh] = React.useState(false);
	const updateRefresh = () => setRefresh(state => !state);

	const paginationProps = usePagination({
		module: 'dems_recipients',
		filter: {
			defaultFilters: [{ filt_field: 'derc_dems_uid', filt_clause: 'equals', filt_value: model.dems_uid }]
		},
		paginator: { defaultNRows: 100 }
	});

	const { selectedItems, setSelectedItems } = useSelection({ module: 'dems_recipients', isSelecting: canEdit });

	function showBounceDialog(bounceType) {
		dispatch(
			showDialogGenericInfo({
				title: t(`DIALOG_INFO_${bounceType}_TITLE`),
				content: t(`DIALOG_INFO_${bounceType}_TEXT`)
			})
		);
	}

	const columns: GridColDef[] = [
		{
			field: 'mail_title',
			headerName: t('DEMS_RECIPIENTS_COLUMN_MAIL'),
			minWidth: 200,
			flex: 4,
			renderCell: ({ value }) => {
				if (value) return ViewTableUtils.renderCellWithTooltip({ value });
				return ViewTableUtils.renderSquareChip({
					value: t('DELETED_VALUE'),
					backgroundColor: theme => theme.palette.error.main,
					icon: <Icon icon="trash-can" size={18} />
				});
			},
			hideable: false
		},
		{
			field: 'mail_table_uid',
			headerName: t('DEMS_RECIPIENTS_COLUMN_ITEM'),
			minWidth: 200,
			flex: 4,
			renderCell: ({ value, row }) =>
				ViewTableUtils.renderCellWithTooltip({
					value:
						row.mail_table === 'people'
							? `${row.peop_title}${row.comp_title ? ` - ${row.comp_title}` : ''}`
							: row.mail_table === 'companies'
								? row.comp_title
								: row.user_title,
					to: { module: row.mail_table, uid: value }
				}),
			sortable: false
		},
		{
			field: 'derc_clicks_count',
			type: 'number',
			headerName: t('DEMS_RECIPIENTS_COLUMN_CLICKS'),
			renderCell: ({ value, row }) =>
				ViewTableUtils.renderSquareChip({
					value: `${value || 0}`,
					backgroundColor: theme => theme.palette.success.main,
					icon: <Icon icon="cursor-default-click-outline" size={18} />,
					onClick:
						(row.derc_clicks || []).length > 0
							? () => dispatch(showDemsRecipientsClickDetailsDialog({ clicks: row.derc_clicks }))
							: undefined,
					disabled: !value
				})
		},
		{
			field: 'derc_open_count',
			type: 'number',
			headerName: t('DEMS_RECIPIENTS_COLUMN_OPENS'),
			renderCell: ({ value, row }) =>
				ViewTableUtils.renderSquareChip({
					value: `${value || 0}`,
					backgroundColor: theme => theme.palette.warning.main,
					icon: <Icon icon="eye" size={18} />,
					onClick:
						(row.derc_opens || []).length > 0
							? () => dispatch(showDemsRecipientsOpenDetailsDialog({ opens: row.derc_opens }))
							: undefined,
					disabled: !value
				})
		},
		{
			field: 'mail_unsubscribed_dems_uid',
			type: 'number',
			headerName: t('DEMS_RECIPIENTS_COLUMN_UNSUBSCRIBED'),
			renderCell: ({ value, row }) =>
				value === row.dems_uid &&
				ViewTableUtils.renderSquareChip({
					value: t('DEMS_RECIPIENTS_COLUMN_UNSUBSCRIBED'),
					backgroundColor: theme => theme.palette.error.main
					// icon: <Icon icon="email-off" size={18} />
				})
		},
		{
			field: 'derc_bounce_type',
			type: 'number',
			headerName: t('DEMS_RECIPIENTS_COLUMN_BOUNCE'),
			renderCell: ({ value }) =>
				ViewTableUtils.renderSquareChip({
					value,
					icon: <Icon icon="alert" size={18} />,
					onClick: () => showBounceDialog(value)
				})
		}
	];

	const onFabClick = () => {
		Utils.executeStored({
			action: 'st_dems_recipients_delete',
			dispatch,
			data: {
				derc_dems_uid: model.dems_uid,
				values: selectedItems.map(item => item.derc_uid)
			},
			successHandler: () => {
				dispatch(Utils.alertSuccessMsg(t));
				setSelectedItems([]);
				updateRefresh();
			}
		});
	};

	return (
		<Grid container alignItems="stretch" spacing={2}>
			{canEdit && (
				<>
					<Grid size={{ md: 8, sm: 6, xs: 12 }}>
						<DemsDetailsSectionAddRecipients
							demsUid={model.dems_uid}
							isLoading={paginationProps.paginatorProps.isLoading}
							updateRefresh={updateRefresh}
						/>
					</Grid>
					<Grid size={{ md: 4, sm: 6, xs: 12 }}>
						<DemsDetailsSectionDeleteRecipients
							demsUid={model.dems_uid}
							isLoading={paginationProps.paginatorProps.isLoading}
							updateRefresh={updateRefresh}
						/>
					</Grid>
				</>
			)}
			<Grid size={12}>
				<ViewTable
					module="dems_recipients"
					columns={columns}
					paginationProps={paginationProps}
					refresh={refresh}
					updateRefresh={updateRefresh}
					slots={{
						footerCustomInfo: ({ additionalInfo }) => (
							<Typography className="p-8" variant="subtitle2">
								{intl.formatMessage(
									{
										id: 'demsRecipientsTable.footerCounters',
										defaultMessage: t('DEMS_RECIPIENTS_TABLE_FOOTER_COUNTERS')
									},
									{
										nCompanies: additionalInfo.n_companies,
										nPeople: additionalInfo.n_people,
										nUsers: additionalInfo.n_users
									}
								)}
							</Typography>
						)
					}}
					selectionProps={canEdit ? { mode: 'multiple', selectedItems: selectedItems } : undefined}
				/>
			</Grid>
			{!isEmpty(selectedItems) && (
				<TabFab label={t('VIEW_ITEM_BUTTON_DELETE')} icon="delete" onClick={onFabClick} />
			)}
		</Grid>
	);
}
